import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import LazyRouteErrorBoundary from './components/LazyRouteErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import PageViewTracker from './components/PageViewTracker';
import { isNativeApp } from './utils/isNativeApp';
import { initNativeApp } from './capacitor/initNativeApp';
import NativeDebugOverlay from './components/NativeDebugOverlay';
import CookieConsentManager from './privacy/CookieConsentManager';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Pages — Public (eager: landing/auth flow)
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Pricing from './pages/Pricing';
import PaymentSuccess from './pages/PaymentSuccess';
import VerifyEmail from './pages/VerifyEmail';
import AuthAction from './pages/AuthAction';
import CompleteProfile from './pages/CompleteProfile';
import LinkGoogleAccount from './pages/LinkGoogleAccount';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import LegalNotesAI from './pages/LegalNotesAI';

// Quiz Live (public) — eager, small
import JoinLive from './quiz/live/JoinLive';
import JoinLiveRoute from './quiz/live/JoinLiveRoute';
import StudentLiveView from './quiz/live/StudentLiveView';

// Bacheca pubblica — eager (deep link)
import BachecaPubblica from './pages/BachecaPubblica';
import BachecaJoin from './pages/BachecaJoin';

// Workspace providers (non-route, keep eager)
import WorkspaceBootstrapper from './workspace/WorkspaceBootstrapper';
import { WorkspaceProvider } from './workspace/state/workspaceStore';
import { UserContextProvider } from './contexts/UserContext';
import { CommunityComposerProvider } from './contexts/CommunityComposerContext';
import { SOCIAL_FEATURES_ENABLED } from './config/featureFlags';

// Pages — Protected (lazy: code-split per route)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Students = lazy(() => import('./pages/Students'));
const CreateStudent = lazy(() => import('./pages/CreateStudent'));
const CreateClass = lazy(() => import('./pages/CreateClass'));
const StudentDetail = lazy(() => import('./pages/StudentDetail'));
const Appello = lazy(() => import('./pages/Appello'));
const DocumentUpload = lazy(() => import('./pages/DocumentUpload'));
const VerbaleGLO = lazy(() => import('./pages/VerbaleGLO'));
const PEIMinisteriale = lazy(() => import('./pages/PEIMinisteriale'));
const VerificaPEIHome = lazy(() => import('./pages/VerificaPEIHome'));
const VerificaPEI = lazy(() => import('./pages/VerificaPEI'));
const AIDidattica = lazy(() => import('./pages/AIDidattica'));
const PresentazioniProNew = lazy(() => import('./pages/PresentazioniProNew'));
const PresentazioniGalleria = lazy(() => import('./pages/PresentazioniGalleria'));
const PresentazioniCanvaLikeDemo = lazy(() => import('./pages/PresentazioniCanvaLikeDemo'));
const CreatePresentation = lazy(() => import('./features/presentations/CreatePresentation'));
const PDPHome = lazy(() => import('./pages/PDPHome'));
const PDPWizard = lazy(() => import('./pages/PDPWizard'));
const CaaPage = lazy(() => import('./caa/CaaPage'));
const AgendaPage = lazy(() => import('./caa/agenda/AgendaPage'));
const LibreriaMateriali = lazy(() => import('./pages/LibreriaMateriali'));
const CommunityFeed = lazy(() => import('./pages/CommunityFeedV2'));
const CommunityContent = lazy(() => import('./pages/CommunityContent'));
const CommunityProfile = lazy(() => import('./pages/CommunityProfile'));
const CommunitySaved = lazy(() => import('./pages/CommunitySaved'));
const CommunityMessagesInbox = lazy(() => import('./pages/CommunityMessagesInbox'));
const CommunityMessagesChat = lazy(() => import('./pages/CommunityMessagesChat'));
const Settings = lazy(() => import('./pages/Settings'));
const DiarioBordo = lazy(() => import('./pages/DiarioBordo'));
const StoricoAnniScolastici = lazy(() => import('./pages/StoricoAnniScolastici'));
const NuovoAnnoScolastico = lazy(() => import('./pages/NuovoAnnoScolastico'));
const OrarioDocente = lazy(() => import('./pages/OrarioDocente'));
const MyContext = lazy(() => import('./pages/MyContext'));
const Calendario = lazy(() => import('./pages/Calendario'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const QuizLiveSolo = lazy(() => import('./pages/QuizLiveSolo'));
const WorkspacePage = lazy(() => import('./workspace/WorkspacePage'));
const BachecaCollaborativa = lazy(() => import('./pages/BachecaCollaborativa'));
const BachecaBoardAdmin = lazy(() => import('./pages/BachecaBoardAdmin'));
const BachecaBoardWall = lazy(() => import('./pages/BachecaBoardWall'));

import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function NativeRootRedirect({ children }) {
  const location = useLocation();
  if (isNativeApp() && (location.pathname === '/' || location.pathname === '')) {
    return <Navigate to="/app/login" replace />;
  }
  return children;
}

function App() {
  useEffect(() => {
    initNativeApp();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <SubscriptionProvider>
              <WorkspaceProvider>
                <WorkspaceBootstrapper />
                <UserContextProvider>
                  <BrowserRouter>
                    <NativeRootRedirect>
                      <CommunityComposerProvider>
                        <CookieConsentManager />
                        <PageViewTracker />
                        <NativeDebugOverlay />
                        <div className="App ds-app-bg">
                          <LazyRouteErrorBoundary>
                            <Suspense fallback={<LoadingSpinner />}>
                            <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/link-account" element={<LinkGoogleAccount />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/payment/success" element={<PaymentSuccess />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/auth/action" element={<AuthAction />} />
                        <Route path="/terms" element={<TermsAndConditions />} />
                        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/cookie-policy" element={<CookiePolicy />} />
                        <Route path="/note-legali-ai" element={<LegalNotesAI />} />

                {/* App routes (Capacitor) */}
                <Route path="/app" element={<Navigate to="/app/login" replace />} />
                <Route path="/app/login" element={<Login />} />
                <Route path="/app/register" element={<Register />} />
                <Route path="/app/link-account" element={<LinkGoogleAccount />} />
                <Route path="/app/verify-email" element={<VerifyEmail />} />
                <Route path="/app/auth/action" element={<AuthAction />} />
                <Route path="/app/pricing" element={<Pricing />} />
                <Route path="/app/payment/success" element={<PaymentSuccess />} />

                {/* Legal routes (Capacitor) */}
                <Route path="/app/terms" element={<TermsAndConditions />} />
                <Route path="/app/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/app/privacy" element={<PrivacyPolicy />} />
                <Route path="/app/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/app/cookie-policy" element={<CookiePolicy />} />
                <Route path="/app/note-legali-ai" element={<LegalNotesAI />} />

                {/* Quiz Live (public) */}
                <Route path="/live/join" element={<JoinLive />} />
                <Route path="/join/:code" element={<JoinLiveRoute />} />
                <Route path="/live/:pin" element={<StudentLiveView />} />
                <Route path="/live/s/:sessionId" element={<StudentLiveView />} />

                {/* Quiz Live (public, Capacitor) */}
                <Route path="/app/live/join" element={<JoinLive />} />
                <Route path="/app/join/:code" element={<JoinLiveRoute />} />
                <Route path="/app/live/:pin" element={<StudentLiveView />} />
                <Route path="/app/live/s/:sessionId" element={<StudentLiveView />} />

                {/* Bacheca collaborativa (public) */}
                <Route path="/b/join" element={<BachecaJoin />} />
                <Route path="/b/:code" element={<BachecaPubblica />} />
                <Route path="/b/:code/full" element={<BachecaPubblica fullBleed />} />
                <Route path="/app/b/join" element={<BachecaJoin />} />
                <Route path="/app/b/:code" element={<BachecaPubblica />} />
                <Route path="/app/b/:code/full" element={<BachecaPubblica fullBleed />} />

                {/* Protected Routes - Docente */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <WorkspacePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/dashboard"
                  element={
                    <ProtectedRoute>
                      <WorkspacePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/workspace"
                  element={
                    <ProtectedRoute>
                      <WorkspacePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/workspace"
                  element={
                    <ProtectedRoute>
                      <WorkspacePage />
                    </ProtectedRoute>
                  }
                />

                {/* Complete Profile */}
                <Route
                  path="/complete-profile"
                  element={
                    <ProtectedRoute allowIncompleteProfile>
                      <CompleteProfile />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/complete-profile"
                  element={
                    <ProtectedRoute allowIncompleteProfile>
                      <CompleteProfile />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/quiz-live"
                  element={
                    <ProtectedRoute>
                      <QuizLiveSolo />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/quiz-live"
                  element={
                    <ProtectedRoute>
                      <QuizLiveSolo />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/bacheca"
                  element={
                    <ProtectedRoute>
                      <BachecaCollaborativa />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/bacheca"
                  element={
                    <ProtectedRoute>
                      <BachecaCollaborativa />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/bacheca/:id"
                  element={
                    <ProtectedRoute>
                      <BachecaBoardAdmin />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/bacheca/:id/wall"
                  element={
                    <ProtectedRoute>
                      <BachecaBoardWall />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/bacheca/:id"
                  element={
                    <ProtectedRoute>
                      <BachecaBoardAdmin />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/bacheca/:id/wall"
                  element={
                    <ProtectedRoute>
                      <BachecaBoardWall />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students"
                  element={
                    <ProtectedRoute>
                      <Students />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/students"
                  element={
                    <ProtectedRoute>
                      <Students />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/new"
                  element={
                    <ProtectedRoute>
                      <CreateStudent />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/classes/new"
                  element={
                    <ProtectedRoute>
                      <CreateClass />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/students/new"
                  element={
                    <ProtectedRoute>
                      <CreateStudent />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/classes/new"
                  element={
                    <ProtectedRoute>
                      <CreateClass />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/:id"
                  element={
                    <ProtectedRoute>
                      <StudentDetail />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/students/:id"
                  element={
                    <ProtectedRoute>
                      <StudentDetail />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/pdp"
                  element={
                    <ProtectedRoute>
                      <PDPHome />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/appello"
                  element={
                    <ProtectedRoute>
                      <Appello />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/pdp"
                  element={
                    <ProtectedRoute>
                      <PDPHome />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/appello"
                  element={
                    <ProtectedRoute>
                      <Appello />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/pdp/:pdpId"
                  element={
                    <ProtectedRoute>
                      <PDPWizard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/pdp/:pdpId"
                  element={
                    <ProtectedRoute>
                      <PDPWizard />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/:id/upload"
                  element={
                    <ProtectedRoute>
                      <DocumentUpload />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/students/:id/upload"
                  element={
                    <ProtectedRoute>
                      <DocumentUpload />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/:studentId/verbale-glo"
                  element={
                    <ProtectedRoute>
                      <VerbaleGLO />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/students/:studentId/verbale-glo"
                  element={
                    <ProtectedRoute>
                      <VerbaleGLO />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/students/:studentId/verbale-glo/:verbaleId"
                  element={
                    <ProtectedRoute>
                      <VerbaleGLO />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/students/:studentId/verbale-glo/:verbaleId"
                  element={
                    <ProtectedRoute>
                      <VerbaleGLO />
                    </ProtectedRoute>
                  }
                />
                
                <Route 
                  path="/pei-ministeriale/:peiId" 
                  element={
                    <ProtectedRoute>
                      <PEIMinisteriale />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/app/pei-ministeriale/:peiId" 
                  element={
                    <ProtectedRoute>
                      <PEIMinisteriale />
                    </ProtectedRoute>
                  } 
                />

                {/* Verifica PEI (SIDI) */}
                <Route
                  path="/pei/verifica"
                  element={
                    <ProtectedRoute>
                      <VerificaPEIHome />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/pei/verifica"
                  element={
                    <ProtectedRoute>
                      <VerificaPEIHome />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/pei/verifica/:studentId"
                  element={
                    <ProtectedRoute>
                      <VerificaPEI />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/pei/verifica/:studentId"
                  element={
                    <ProtectedRoute>
                      <VerificaPEI />
                    </ProtectedRoute>
                  }
                />

                {/* AI Didattica - Presentazioni */}
                <Route
                  path="/ai-didattica/presentazioni/new"
                  element={
                    <ProtectedRoute>
                      <PresentazioniProNew />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ai-didattica/presentazioni"
                  element={
                    <ProtectedRoute>
                      <PresentazioniGalleria />
                    </ProtectedRoute>
                  }
                />

                {/* Presentazioni — sezione standalone nel menu */}
                <Route
                  path="/presentazioni/new"
                  element={
                    <ProtectedRoute>
                      <PresentazioniProNew />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/presentazioni"
                  element={
                    <ProtectedRoute>
                      <PresentazioniGalleria />
                    </ProtectedRoute>
                  }
                />

                <Route 
                  path="/ai-didattica" 
                  element={
                    <ProtectedRoute>
                      <AIDidattica />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/ai-didattica/presentazioni/new"
                  element={
                    <ProtectedRoute>
                      <PresentazioniProNew />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/ai-didattica/presentazioni"
                  element={
                    <ProtectedRoute>
                      <PresentazioniGalleria />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/presentazioni/new"
                  element={
                    <ProtectedRoute>
                      <PresentazioniProNew />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/presentazioni"
                  element={
                    <ProtectedRoute>
                      <PresentazioniGalleria />
                    </ProtectedRoute>
                  }
                />

                <Route 
                  path="/app/ai-didattica" 
                  element={
                    <ProtectedRoute>
                      <AIDidattica />
                    </ProtectedRoute>
                  }
                />

                {/* CAA (Pittogrammi) */}
                <Route
                  path="/caa"
                  element={
                    <ProtectedRoute>
                      <CaaPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/caa/agenda-visiva"
                  element={
                    <ProtectedRoute>
                      <AgendaPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/caa"
                  element={
                    <ProtectedRoute>
                      <CaaPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/caa/agenda-visiva"
                  element={
                    <ProtectedRoute>
                      <AgendaPage />
                    </ProtectedRoute>
                  }
                />

                {/* Presentazioni Canva-like (demo) */}
                <Route
                  path="/presentazioni-demo"
                  element={
                    <ProtectedRoute>
                      <PresentazioniCanvaLikeDemo />
                    </ProtectedRoute>
                  }
                />

                {/* Presentazioni AI (beta) */}
                <Route
                  path="/presentazioni-ai"
                  element={
                    <ProtectedRoute>
                      <CreatePresentation />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/presentazioni-demo"
                  element={
                    <ProtectedRoute>
                      <PresentazioniCanvaLikeDemo />
                    </ProtectedRoute>
                  }
                />

                {/* Presentazioni AI (beta) */}
                <Route
                  path="/app/presentazioni-ai"
                  element={
                    <ProtectedRoute>
                      <CreatePresentation />
                    </ProtectedRoute>
                  }
                />

                {/* Libreria Materiali */}
                <Route 
                  path="/libreria-materiali" 
                  element={
                    <ProtectedRoute>
                      <LibreriaMateriali />
                    </ProtectedRoute>
                  }
                />

                <Route 
                  path="/app/libreria-materiali" 
                  element={
                    <ProtectedRoute>
                      <LibreriaMateriali />
                    </ProtectedRoute>
                  }
                />

                {/* Community didattica + Messaggi (condivisione/riuso, NO marketplace).
                    Nascoste al lancio via SOCIAL_FEATURES_ENABLED: il codice resta
                    intatto per essere riattivato più avanti, ma le rotte reindirizzano
                    alla dashboard finché il flag è spento. */}
                {SOCIAL_FEATURES_ENABLED ? (
                  <>
                    <Route
                      path="/community"
                      element={
                        <ProtectedRoute>
                          <CommunityFeed />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/community/messages"
                      element={
                        <ProtectedRoute>
                          <CommunityMessagesInbox />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/community/messages/:conversationId"
                      element={
                        <ProtectedRoute>
                          <CommunityMessagesChat />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/community/c/:contentId"
                      element={
                        <ProtectedRoute>
                          <CommunityContent />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/community/u/:userId"
                      element={
                        <ProtectedRoute>
                          <CommunityProfile />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/community/saved"
                      element={
                        <ProtectedRoute>
                          <CommunitySaved />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/app/community"
                      element={
                        <ProtectedRoute>
                          <CommunityFeed />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/app/community/messages"
                      element={
                        <ProtectedRoute>
                          <CommunityMessagesInbox />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/app/community/messages/:conversationId"
                      element={
                        <ProtectedRoute>
                          <CommunityMessagesChat />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/app/community/c/:contentId"
                      element={
                        <ProtectedRoute>
                          <CommunityContent />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/app/community/u/:userId"
                      element={
                        <ProtectedRoute>
                          <CommunityProfile />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/app/community/saved"
                      element={
                        <ProtectedRoute>
                          <CommunitySaved />
                        </ProtectedRoute>
                      }
                    />
                  </>
                ) : (
                  <>
                    <Route path="/community/*" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/app/community/*" element={<Navigate to="/app/dashboard" replace />} />
                  </>
                )}

                {/* Settings */}
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/my-context"
                  element={
                    <ProtectedRoute>
                      <MyContext />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/my-context"
                  element={
                    <ProtectedRoute>
                      <MyContext />
                    </ProtectedRoute>
                  }
                />

                {/* Diario di Bordo */}
                <Route
                  path="/diario"
                  element={
                    <ProtectedRoute>
                      <DiarioBordo />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/diario"
                  element={
                    <ProtectedRoute>
                      <DiarioBordo />
                    </ProtectedRoute>
                  }
                />

                {/* Storico anni scolastici + passaggio anno */}
                <Route
                  path="/storico-anni-scolastici"
                  element={
                    <ProtectedRoute>
                      <StoricoAnniScolastici />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/app/storico-anni-scolastici"
                  element={
                    <ProtectedRoute>
                      <StoricoAnniScolastici />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/nuovo-anno-scolastico"
                  element={
                    <ProtectedRoute>
                      <NuovoAnnoScolastico />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/app/nuovo-anno-scolastico"
                  element={
                    <ProtectedRoute>
                      <NuovoAnnoScolastico />
                    </ProtectedRoute>
                  }
                />

                {/* Calendario */}
                <Route
                  path="/calendario"
                  element={
                    <ProtectedRoute>
                      <Calendario />
                    </ProtectedRoute>
                  }
                />

                {/* Orario docente */}
                <Route
                  path="/orario"
                  element={
                    <ProtectedRoute>
                      <OrarioDocente />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/calendario"
                  element={
                    <ProtectedRoute>
                      <Calendario />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/app/orario"
                  element={
                    <ProtectedRoute>
                      <OrarioDocente />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Panel - Hidden route */}
                <Route path="/admin-panel" element={<AdminPanel />} />

                <Route path="/app/admin-panel" element={<AdminPanel />} />

                {/* Catch-all per /app */}
                <Route path="/app/*" element={<Navigate to="/app/login" replace />} />

                        {/* Catch all */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                          </Suspense>
                          </LazyRouteErrorBoundary>
                        </div>
                      </CommunityComposerProvider>
                    </NativeRootRedirect>
                  </BrowserRouter>
                </UserContextProvider>
                <Toaster position="top-right" />
                {process.env.NODE_ENV !== 'production' ? (
                  <ReactQueryDevtools initialIsOpen={false} />
                ) : null}
              </WorkspaceProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
